/**
 * PhotoCapturePanel Component
 *
 * Shows live camera stream with capture button.
 * Captures photos and adds them to AddContext.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui';
import { useAdd } from '@/contexts/AddContext';
import { createLogger } from '@/lib/logger';

const log = createLogger('PhotoCapturePanel');

/**
 * Capture photo from video stream and compress to JPEG
 */
function capturePhotoFromStream(video: HTMLVideoElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const maxWidth = 1920;
    const aspectRatio = video.videoHeight / video.videoWidth;

    canvas.width = Math.min(video.videoWidth, maxWidth);
    canvas.height = canvas.width * aspectRatio;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      'image/jpeg',
      0.85
    );
  });
}

export const PhotoCapturePanel: React.FC = () => {
  const { t } = useTranslation();
  const { addPhoto, duration } = useAdd();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [error, setError] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Rear camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreamActive(true);
        log.info('Camera stream started');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      log.error('Failed to start camera', err as Error);

      if (errorMessage.includes('Permission denied') || errorMessage.includes('NotAllowedError')) {
        setError(t('cameraPermissionDenied'));
      } else {
        setError(t('photoCaptureFailed'));
      }
    }
  }, [t]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setIsStreamActive(false);
      log.info('Camera stream stopped');
    }
  }, []);

  // Capture photo from stream
  const handleCapturePhoto = useCallback(async () => {
    if (!videoRef.current || !isStreamActive) {
      return;
    }

    try {
      setIsCapturing(true);
      const blob = await capturePhotoFromStream(videoRef.current);

      // Add photo to recording context with current timestamp
      addPhoto(blob);

      toast.success(t('photoCaptured'));
      log.info('Photo captured', { timestamp: duration });
    } catch (err) {
      log.error('Failed to capture photo', err as Error);
      toast.error(t('photoCaptureFailed'));
    } finally {
      setIsCapturing(false);
    }
  }, [isStreamActive, addPhoto, duration, t]);

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        {t('create.capturePhotoDescription')}
      </p>

      {/* Camera Preview */}
      <div className="relative bg-slate-900 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Loading overlay */}
        {!isStreamActive && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-indigo-600 mx-auto mb-3" />
              <p className="text-white text-sm">{t('create.startingCamera')}</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90">
            <div className="text-center px-6">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-white mb-4">{error}</p>
              <Button onClick={startCamera} variant="primary" size="md">
                {t('create.retryCamera')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Capture Button */}
      <div className="flex justify-center">
        <Button
          onClick={handleCapturePhoto}
          disabled={!isStreamActive || isCapturing}
          variant="gradient"
          size="lg"
          shape="rounded"
        >
          <Camera className="w-5 h-5" />
          {isCapturing ? t('create.capturing') : t('create.capturePhoto')}
        </Button>
      </div>
    </div>
  );
};
